import axios from "axios";
import Swal from "sweetalert2";
import { format } from "date-fns";
import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  saleCardAmount,
  saleCardName,
  saleCash,
  saleMfsAmount,
  saleMfsName,
  selectChangeAmount,
  selectCustomerId,
  selectDueAmount,
  selectInvoice,
} from "../../../features/paymentSlice";
import { useAddPaymentMutation } from "../../../services/paymentApi";
import { signInUser } from "../../Utility/Auth";
import { notify } from "../../Utility/Notify";
import sendMessage from "../../Utility/smsSystem";
import SelectCustomerLedger from "../CustomSelect/selectCustomerLedger";

const BillPayModal = ({ title, onShow, handleClose }) => {
  const auth = signInUser() || {};
  const { aamarId } = auth;
  const BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:5001/api/";

  const [selectCustomer, setSelectCustomer] = useState();
  const [invoices, setInvoices] = useState([]);
  const paymentData = useSelector((state) => state.paymentReducer);
  const dispatch = useDispatch();

  const getCustomerId = (cust) => {
    if (!cust) return "";
    if (Array.isArray(cust)) {
      const first = cust[0];
      if (!first) return "";
      if (typeof first === "string") return first;
      return first._id || first.id || first.value || first.option || "";
    }
    if (typeof cust === "object") {
      return cust._id || cust.id || cust.value || cust.option || "";
    }
    return String(cust);
  };

  const getDueInvoice = async (customer) => {
    const custId = getCustomerId(customer);
    if (!custId) return;
    try {
      const effectiveAamarId =
        aamarId ||
        auth?.aamarId ||
        auth?.storeSettings?.aamarId ||
        localStorage.getItem("aamarId") ||
        "";
      const endpoint =
        effectiveAamarId &&
        effectiveAamarId !== "undefined" &&
        effectiveAamarId !== "null"
          ? `sale/dueByCustomer/${custId}/${effectiveAamarId}`
          : `sale/dueByCustomer/${custId}`;
      const cleanUrl = BASE_URL.endsWith("/")
        ? `${BASE_URL}${endpoint}`
        : `${BASE_URL}/${endpoint}`;
      const result = await axios.get(cleanUrl);
      setInvoices(result.data || { sales: [], totalDue: 0 });
    } catch (err) {
      console.error("Error fetching due invoices:", err);
      setInvoices({ sales: [], totalDue: 0 });
    }
  };

  const getInvoiceDue = (item) => {
    if (!item) return 0;
    if (item.dueAmount !== undefined && item.dueAmount !== null) {
      return Number(item.dueAmount) || 0;
    }
    const gross = Number(
      item.grossTotalRound ?? item.grossTotal ?? item.total ?? 0
    );
    let received = Number(item.totalReceived ?? 0);
    if (
      !received &&
      item.paidAmount &&
      (item.paidAmount.cash ||
        item.paidAmount.card?.amount ||
        item.paidAmount.mfs?.amount)
    ) {
      received =
        (Number(item.paidAmount.cash) || 0) +
        (Number(item.paidAmount.card?.amount) || 0) +
        (Number(item.paidAmount.mfs?.amount) || 0);
    }
    if (received > 0 && received < gross) {
      return gross - received;
    }
    return gross;
  };

  const minDueCalculation = () => {
    let dueAmount = 0;
    if (paymentData.invoices?.length > 0) {
      paymentData?.invoices?.forEach((id) => {
        const match = invoices?.sales?.find((item) => item._id === id);
        if (match) {
          dueAmount += getInvoiceDue(match);
        }
      });
    }

    dispatch(selectDueAmount(dueAmount));
  };

  useEffect(() => {
    const custId = getCustomerId(selectCustomer);
    if (custId && typeof custId === "string" && /^[0-9a-fA-F]{24}$/.test(custId)) {
      getDueInvoice(custId);
      dispatch(selectCustomerId(custId));
    } else {
      setInvoices({ sales: [], totalDue: 0 });
    }
    dispatch(selectInvoice([]));
  }, [selectCustomer, onShow]);

  useEffect(() => {
    minDueCalculation();
  }, [paymentData.invoices, invoices]);

  useEffect(() => {
    const changeAmount =
      (paymentData.dueAmount || 0) -
      ((parseInt(paymentData.paidAmount?.cash?.amount) || 0) +
        (parseInt(paymentData.paidAmount?.mfs?.amount) || 0) +
        (parseInt(paymentData.paidAmount?.card?.amount) || 0));
    dispatch(selectChangeAmount(changeAmount));
  }, [paymentData.paidAmount, paymentData.dueAmount]);

  const handleInvoiceSelect = (id) => {
    let newInvoices = [...paymentData.invoices];

    if (newInvoices.includes(id)) {
      newInvoices = newInvoices.filter((invoiceId) => invoiceId !== id);
      dispatch(selectInvoice(newInvoices));
    } else {
      newInvoices.push(id);
      dispatch(selectInvoice(newInvoices));
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  function getInvoiceIdsFromIds() {
    const filteredObjects = (invoices?.sales || []).filter((obj) =>
      paymentData.invoices.includes(obj._id)
    );
    const invoiceIds = filteredObjects.map((obj) => obj.invoiceId || obj._id);
    return invoiceIds;
  }

  const [createNew] = useAddPaymentMutation();

  const createPayment = async () => {
    try {
      const custId = getCustomerId(selectCustomer);
      if (!custId) {
        notify("Please select a customer first.", "error");
        return;
      }

      if (!paymentData?.invoices || paymentData.invoices.length === 0) {
        notify("Please select at least one due invoice to pay.", "error");
        return;
      }

      const result = await Swal.fire({
        title: "Confirm Due Payment",
        text: "Are you sure you want to process this payment and print the bill?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, pay & print!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        const billerId =
          auth?.id ||
          auth?._id ||
          auth?.userId ||
          paymentData?.billerId;

        const currentTotalPaid =
          (parseFloat(paymentData?.paidAmount?.cash?.amount) || 0) +
          (parseFloat(paymentData?.paidAmount?.card?.amount) || 0) +
          (parseFloat(paymentData?.paidAmount?.mfs?.amount) || 0);

        let finalPaidAmount = { ...paymentData.paidAmount };
        if (currentTotalPaid === 0 && (paymentData?.dueAmount || 0) > 0) {
          finalPaidAmount = {
            ...finalPaidAmount,
            cash: {
              ...(finalPaidAmount.cash || {}),
              amount: paymentData.dueAmount,
            },
          };
        }

        const payload = {
          ...paymentData,
          paidAmount: finalPaidAmount,
          customerId: custId,
          billerId: billerId || null,
          authorizedUser: paymentData?.authorizedUser || null,
          aamarId: aamarId || auth?.aamarId,
        };

        const payment = await createNew(payload);
        console.log("Payment response:", payment);

        const isSuccess =
          payment?.data?.status === 200 ||
          payment?.data?.status === 201 ||
          payment?.data?.success ||
          payment?.data?.id ||
          payment?.data?._id ||
          (!payment?.error && payment?.data);

        if (isSuccess) {
          const selectedInvoices =
            (invoices?.sales || []).filter((item) =>
              paymentData.invoices.includes(item._id)
            );

          const targetPrintId =
            selectedInvoices[0]?._id ||
            selectedInvoices[0]?.invoiceId ||
            paymentData.invoices[0];

          const customerPhone =
            (typeof selectCustomer === "object" ? selectCustomer?.phone : "") ||
            selectedInvoices[0]?.customerPhone;

          if (customerPhone) {
            const message = `Payment of ${paymentData?.dueAmount || 0} TK received successfully. Thank you for your payment.`;
            sendMessage(customerPhone, message);
          }

          notify("Payment Successful!", "success");

          // Close due payment modal
          handleClose();

          // Open bill print option window
          if (targetPrintId) {
            window.open(`/print/${targetPrintId}`, "_blank");
          }

          // Interactive prompt with Print Bill option
          Swal.fire({
            title: "Payment Successful!",
            text: "Due bill payment completed. Click 'Print Bill' if you wish to print the bill receipt.",
            icon: "success",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Print Bill",
            cancelButtonText: "Close",
          }).then((printRes) => {
            if (printRes.isConfirmed && targetPrintId) {
              window.open(`/print/${targetPrintId}`, "_blank");
            }
          });
        } else {
          const errorMsg =
            payment?.error?.data?.message ||
            payment?.error?.message ||
            "Payment failed. Please try again.";
          notify(errorMsg, "error");
        }
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      notify("Payment failed. Please try again.", "error");
    }
  };


  return (
    <Modal size="lg" show={onShow} onHide={handleClose}>
      <Form>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid>
            <Row>
              <Col xs={12} className="mb-3">
                <b>Select Customer:</b>
                <SelectCustomerLedger setSelcetCustomer={setSelectCustomer} />
              </Col>
            </Row>
            <div className="card mt-2 p-2">
              <Row className="g-3">
                {/* Due Invoices Section */}
                <Col xs={12} md={7}>
                  <b>Due Invoices</b>
                  <hr />
                  <div className="">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Invoice No</th>
                          <th>Date</th>
                          <th className="text-end">Due Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectCustomer ? (
                          invoices?.sales?.length > 0 ? (
                            invoices?.sales?.map((invoice) => (
                              <tr key={invoice?._id}>
                                <td>
                                  <Form.Check
                                    inline
                                    checked={paymentData.invoices.includes(
                                      invoice._id
                                    )}
                                    onChange={() =>
                                      handleInvoiceSelect(invoice._id)
                                    }
                                  />
                                </td>
                                <td>
                                  <b>{invoice.invoiceId}</b>
                                </td>
                                <td>
                                  {invoice.createdAt
                                    ? format(
                                        new Date(invoice.createdAt),
                                        "MM/dd/yyyy"
                                      )
                                    : "N/A"}
                                </td>
                                <td className="text-end">
                                  {getInvoiceDue(invoice)} TK
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center">
                                <Icons.SelectorOutline /> No Due Found
                              </td>
                            </tr>
                          )
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center">
                              <Icons.SelectorOutline /> Please Select a Customer
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Col>
                {/* Due Details Section */}
                <Col xs={12} md={5}>
                  <b>Due Details</b>
                  <hr />
                  <p>
                    <b>Due Invoice:</b>{" "}
                    <span className="float-end">
                      {invoices?.sales?.length || 0}
                    </span>
                    <br />
                    <b>Paid Invoice:</b>{" "}
                    <span className="float-end">
                      {paymentData?.invoices?.length}
                    </span>
                    <br />
                    <b>Total Due Amount:</b>{" "}
                    <span className="float-end">
                      {invoices?.totalDue !== undefined
                        ? invoices.totalDue
                        : (invoices?.sales || []).reduce(
                            (acc, inv) => acc + getInvoiceDue(inv),
                            0
                          )}{" "}
                      TK
                    </span>
                    <br />
                    <b>Paid Amount:</b>{" "}
                    <span className="float-end">
                      {paymentData?.dueAmount || 0} TK
                    </span>
                  </p>
                  {/* Cash Section */}
                  <Row>
                    <Col xs={6}>
                      <b>Cash Received:</b>
                    </Col>
                    <Col xs={6}>
                      <input
                        type="number"
                        onChange={(e) =>
                          dispatch(
                            saleCash(e.target.value > 0 ? e.target.value : 0)
                          )
                        }
                        id="cashAmount"
                        min={0}
                        value={paymentData.paidAmount.cash.amount}
                        className="form-control"
                      />
                    </Col>
                  </Row>
                  {/* Card Section */}
                  <Row className="mt-3">
                    <Col xs={4}>
                      <b>Card:</b>
                    </Col>
                    <Col xs={8}>
                      <Form.Select
                        onChange={(e) => dispatch(saleCardName(e.target.value))}
                      >
                        <option value="visa">Visa</option>
                        <option value="masterCard">Master Card</option>
                        <option value="MTB">MTB</option>
                        <option value="CITY">CITY</option>
                        <option value="DBBL">DBBL</option>
                        <option value="EBL">EBL</option>
                        <option value="BRAC">BRAC</option>
                      </Form.Select>
                      <input
                        type="number"
                        defaultValue={0}
                        onChange={(e) =>
                          dispatch(
                            saleCardAmount(
                              parseInt(e.target.value > 0 ? e.target.value : 0)
                            )
                          )
                        }
                        min={0}
                        value={paymentData.paidAmount.card.amount}
                        className="form-control mt-2"
                      />
                    </Col>
                  </Row>
                  {/* MFS Section */}
                  <Row className="mt-3">
                    <Col xs={4}>
                      <b>MFS:</b>
                    </Col>
                    <Col xs={8}>
                      <Form.Select
                        onChange={(e) => dispatch(saleMfsName(e.target.value))}
                      >
                        <option value="bKash">Bkash</option>
                        <option value="Nagad">Nagad</option>
                        <option value="Upay">Upay</option>
                        <option value="Rocket">Rocket</option>
                      </Form.Select>
                      <input
                        type="number"
                        id="mfsAmount"
                        defaultValue={0}
                        onChange={(e) =>
                          dispatch(
                            saleMfsAmount(
                              e.target.value > 0 ? e.target.value : 0
                            )
                          )
                        }
                        value={paymentData.paidAmount.mfs.amount}
                        className="form-control mt-2"
                      />
                    </Col>
                  </Row>
                  <hr />
                  <p>
                    <b>Minimum Due:</b>{" "}
                    <span className="float-end">
                      {paymentData.dueAmount || 0} TK
                    </span>
                    <br />
                    <b>Change Amount:</b>{" "}
                    <span className="float-end">
                      {paymentData.changeAmount || 0} TK
                    </span>
                    <br />
                    <b>Remaining Due:</b>{" "}
                    <span className="float-end">
                      {Math.max(0, (invoices?.totalDue || 0) - (paymentData?.dueAmount || 0))} TK
                    </span>
                  </p>
                  <div className="text-end">
                    <Button
                      variant="dark"
                      disabled={paymentData?.invoices?.length <= 0}
                      onClick={createPayment}
                    >
                      <Icons.CurrencyBangladeshi /> Make Payment
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
      <Toaster position="bottom-right" />
    </Modal>
  );
};

export default BillPayModal;
